/**
 * @file TypeORM entities for the calendar library.
 * These entities are for server-side use only and require Node.js/TypeORM.
 */

import { Entity, PrimaryGeneratedColumn, Column, ManyToOne, OneToMany, CreateDateColumn, UpdateDateColumn, JoinColumn } from 'typeorm';

/**
 * Represents a calendar.
 */
@Entity()
export class Calendar {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  name!: string;

  @Column({ nullable: true })
  description?: string;

  /**
   * The type of calendar.
   * 'individual' - A personal calendar for a single user.
   * 'group' - A shared calendar for a team or band.
   */
  @Column()
  type!: string;
}

/**
 * Represents a venue or location for events.
 */
@Entity()
export class Venue {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  name!: string;

  @Column({ nullable: true })
  address?: string;

  @Column({ nullable: true })
  city?: string;

  @Column({ nullable: true })
  state?: string;

  @Column({ nullable: true })
  country?: string;

  @Column({ nullable: true })
  website?: string;

  @Column({ nullable: true })
  contactName?: string;

  @Column({ nullable: true })
  contactEmail?: string;

  @Column({ nullable: true })
  contactPhone?: string;

  @Column({ nullable: true })
  notes?: string;
}

/**
 * Represents a person or organization (student, band member, promoter, etc.)
 */
@Entity()
export class Contact {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  name!: string;

  @Column({ nullable: true })
  email?: string;

  @Column({ nullable: true })
  phone?: string;

  @Column({ nullable: true })
  role?: string; // 'student', 'band-member', 'promoter', 'sound-engineer', etc.

  @Column({ nullable: true })
  notes?: string;
}

/**
 * Represents a calendar event with musician-specific capabilities.
 */
@Entity()
export class Event {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  summary!: string;

  @Column({ nullable: true })
  description?: string;

  @Column()
  start!: Date;

  @Column()
  end!: Date;

  /**
   * An RFC 5545 recurrence rule string.
   * @see https://icalendar.org/rrule-tool.html
   * @example 'FREQ=WEEKLY;BYDAY=MO,WE,FR;UNTIL=20251231T235959Z'
   */
  @Column({ nullable: true })
  recurrenceRule?: string;

  /**
   * Event type: 'gig', 'lesson', 'audition', 'practice', 'rehearsal', 'recording', 'meeting', etc.
   */
  @Column()
  type!: string;

  // Musician-specific fields
  @Column({ nullable: true })
  genre?: string; // 'Jazz', 'Classical', 'Rock', etc.

  @Column({ nullable: true })
  instrument?: string; // Primary instrument for this event

  @Column({ nullable: true })
  difficulty?: string; // 'Beginner', 'Intermediate', 'Advanced', 'Professional'

  @Column({ nullable: true })
  repertoire?: string; // Songs/pieces to be performed or practiced

  @Column({ nullable: true })
  setList?: string; // JSON string of songs in order

  @Column({ nullable: true })
  equipmentNeeded?: string; // JSON array of required equipment

  @Column({ nullable: true })
  dresscode?: string; // 'Formal', 'Casual', 'Black tie', etc.

  @Column({ nullable: true })
  soundcheckTime?: Date; // For gigs

  @Column({ nullable: true })
  loadInTime?: Date; // For gigs

  @Column({ nullable: true })
  paymentStatus?: string; // 'Pending', 'Paid', 'Overdue', 'Cancelled'

  @Column({ nullable: true })
  paymentDueDate?: Date;

  @Column({ nullable: true })
  studentLevel?: string; // For lessons: 'Grade 1', 'Grade 8', 'Diploma', etc.

  @Column({ nullable: true })
  lessonFocus?: string; // For lessons: 'Technique', 'Theory', 'Repertoire', etc.

  @Column({ nullable: true })
  auditionPiece?: string; // For auditions

  @Column({ nullable: true })
  auditionRequirements?: string; // For auditions

  @Column({ nullable: true })
  practiceGoals?: string; // For practice sessions

  @Column({ nullable: true })
  rehearsalNotes?: string; // For rehearsals

  @Column({ nullable: true })
  status?: string; // 'Confirmed', 'Tentative', 'Cancelled', 'Completed'

  @CreateDateColumn()
  createdAt!: Date;

  @UpdateDateColumn()
  updatedAt!: Date;

  // Relationships
  @ManyToOne(() => Calendar)
  @JoinColumn()
  calendar!: Calendar;

  @ManyToOne(() => Venue, { nullable: true })
  @JoinColumn()
  venue?: Venue;

  @ManyToOne(() => Contact, { nullable: true })
  @JoinColumn()
  primaryContact?: Contact; // Main contact for this event (student, promoter, etc.)

  @OneToMany(() => EventIncome, (income) => income.event, { cascade: true })
  income?: EventIncome[];

  @OneToMany(() => EventExpense, (expense) => expense.event, { cascade: true })
  expenses?: EventExpense[];
}

/**
 * Represents income for an event (gig fees, lesson payments, etc.)
 */
@Entity()
export class EventIncome {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  description!: string; // 'Door charge', 'Flat fee', 'Merch sales', 'Tips', etc.

  @Column('decimal', { precision: 10, scale: 2 })
  amount!: number;

  @Column({ default: 'AUD' })
  currency!: string;

  @Column({ nullable: true })
  notes?: string;

  @ManyToOne(() => Event, (event) => event.income)
  @JoinColumn()
  event!: Event;
}

/**
 * Represents expenses for an event (gear hire, travel, food, etc.)
 */
@Entity()
export class EventExpense {
  @PrimaryGeneratedColumn('uuid')
  id!: string;

  @Column()
  description!: string; // 'Gear hire', 'Food & bev', 'Marketing', 'Travel', etc.

  @Column('decimal', { precision: 10, scale: 2 })
  amount!: number;

  @Column({ default: 'AUD' })
  currency!: string;

  @Column({ nullable: true })
  receipt?: string; // Path to receipt image/file

  @Column({ nullable: true })
  notes?: string;

  @ManyToOne(() => Event, (event) => event.expenses)
  @JoinColumn()
  event!: Event;
} 